/**
 * Cleans the retrieved staff by removing staff who left school
 * @param {Array} staff - Array of staff objects
 * @returns {Array} - Filtered array of staff
 */
export const cleanStaff = (staff) => {
  if (!Array.isArray(staff)) return [];
  
  return staff.filter(staffMember => {
    // Remove staff who are blocked with LEAVE_SCHOOL reason
    if (staffMember.blocked && staffMember.blockReason === 'LEAVE_SCHOOL') {
      return false;
    }
    return true;
  });
};

/**
 * Gets the status information for a staff member
 * @param {Object} staffMember - Staff member object
 * @param {Object} baseColors - Base color theme
 * @param {boolean} isDark - Dark mode flag
 * @returns {Object} - Status object with label, color, and bgColor
 */
export const getStaffStatus = (staffMember, baseColors, isDark) => {
  if (!staffMember.blocked) {
    return {
      label: 'Active',
      color: baseColors.success,
      bgColor: isDark ? 'bg-green-900' : 'bg-green-100'
    };
  }

  switch (staffMember.blockReason) {
    case 'DISMISSED':
      return {
        label: 'Dismissed',
        color: baseColors.error,
        bgColor: isDark ? 'bg-red-900' : 'bg-red-100'
      };
    case 'ON_LEAVE':
      return {
        label: 'On Leave',
        color: baseColors.warning,
        bgColor: isDark ? 'bg-yellow-900' : 'bg-yellow-100'
      };
    case 'OTHER':
      return {
        label: 'Blocked',
        color: baseColors.error,
        bgColor: isDark ? 'bg-gray-900' : 'bg-gray-100'
      };
    default:
      return {
        label: 'Blocked',
        color: baseColors.error,
        bgColor: isDark ? 'bg-gray-900' : 'bg-gray-100'
      };
  }
};

/**
 * Gets the department from staff type or defaults to 'General'
 * @param {Object} staffMember - Staff member object
 * @returns {string} - Department name
 */
export const getStaffDepartment = (staffMember) => {
  if (!staffMember) return 'General';
  
  const staffType = staffMember.type;
  if (staffType === undefined || staffType === null) return 'General';
  
  // Map staff type integers to departments
  // 0 - teacher, 1 - student, 2 - principal, 3 - office, 4 - headmistress, 5 - vice-principal, 6 - developer/admin, 7 - alumni, 8 - Bus Staff
  const departmentMap = {
    0: 'Teaching',           // teacher
    1: 'Student',            // student (shouldn't appear in staff but included for completeness)
    2: 'Administration',     // principal
    3: 'Administration',     // office
    4: 'Administration',     // headmistress
    5: 'Administration',     // vice-principal
    6: 'Administration',  // developer, admin
    7: 'Alumni',             // alumni
    8: 'Transportation'      // Bus Staff
  };
  
  return departmentMap[staffType] || 'General';
};

/**
 * Gets the role title for display
 * @param {Object} staffMember - Staff member object
 * @returns {string} - Role title
 */
export const getStaffRole = (staffType) => {
  if (!staffType) return '...';
  
  if (staffType === undefined || staffType === null) return 'Staff';
  
  // Map staff type integers to role titles
  // 0 - teacher, 1 - student, 2 - principal, 3 - office, 4 - headmistress, 5 - vice-principal, 6 - developer/admin, 7 - alumni, 8 - Bus Staff
  const roleMap = {
    0: 'Staff',
    1: 'Student',
    2: 'Principal',
    3: 'Office Staff',
    4: 'Headmistress',
    5: 'Vice Principal',
    6: 'Admin',
    7: 'Alumni',
    8: 'Bus Staff',
    9: 'Management',
    10: 'CEO',
    11: 'COO'
  };
  
  return roleMap[staffType] || 'Staff';
};

/**
 * Formats phone number for display
 * @param {string|number} phone - Phone number
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return 'N/A';
  return phone.toString();
};

/**
 * Formats date for display
 * @param {Date|Object|string} date - Date object, Firestore timestamp, or string
 * @returns {string} - Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';
  
  // Handle Firestore Timestamp
  if (date.toDate && typeof date.toDate === 'function') {
    return date.toDate().toLocaleDateString();
  }
  
  // Handle regular Date object
  if (date instanceof Date) {
    return date.toLocaleDateString();
  }
  
  // Handle string dates
  if (typeof date === 'string') {
    return new Date(date).toLocaleDateString();
  }
  
  return 'N/A';
};

/**
 * Gets unique departments from staff array
 * @param {Array} staff - Array of staff objects
 * @returns {Array} - Array of unique department names
 */
export const getUniqueDepartments = (staff) => {
  if (!Array.isArray(staff)) return [];
  
  const departments = staff.map(staffMember => getStaffDepartment(staffMember));
  return [...new Set(departments)].filter(dept => dept && dept !== 'General').sort();
}; 